import { Logic } from '~common/ui-logic/logic'
import { executeTask } from '~common/ui-logic/tasks'
import type { TaskState } from '~/services/cache/types'
import type { AppContextType } from '~/app-context'
import { extractDomainFromEmail } from '~common/favicon-fetcher'

export interface VendorDisplayLogicDependencies {
    vendorEmail: string
}

export interface VendorDisplayLogicState {
    loadState: TaskState
    faviconUrl: string | null
    vendorDomain: string
    isLoadingFavicon: boolean
}

export class VendorDisplayLogic extends Logic<
    VendorDisplayLogicDependencies & AppContextType,
    VendorDisplayLogicState
> {
    getInitialState = (): VendorDisplayLogicState => ({
        loadState: 'pristine',
        faviconUrl: null,
        vendorDomain: extractDomainFromEmail(this.deps.vendorEmail),
        isLoadingFavicon: false,
    })

    async initialize() {
        await executeTask(this, 'loadState', async () => {
            await this.loadFavicon()
        })
    }

    private async loadFavicon(): Promise<void> {
        try {
            // Start loading indicator
            this.setState({
                isLoadingFavicon: true,
            })

            const faviconUrl =
                await this.deps.services.favicon.getFaviconForEmail(
                    this.deps.vendorEmail,
                )

            this.setState({
                faviconUrl,
                isLoadingFavicon: false,
            })
        } catch (error) {
            console.warn(
                `Failed to load favicon for ${this.deps.vendorEmail}:`,
                error,
            )
            // Keep faviconUrl as null on error and stop loading
            this.setState({
                faviconUrl: null,
                isLoadingFavicon: false,
            })
        }
    }

    async refreshFavicon(): Promise<void> {
        try {
            const faviconUrl = await this.deps.services.favicon.refreshFavicon(
                this.state.vendorDomain,
            )

            this.setState({
                faviconUrl,
            })
        } catch (error) {
            console.warn(
                `Failed to refresh favicon for ${this.deps.vendorEmail}:`,
                error,
            )
        }
    }
}
